<template>
  <div class="sidebar-pinned">
    <Navbar />

    <!-- ── Page Lock Overlay ── -->
    <div v-if="pageLock.locked && !pageLock.isOwner" class="page-lock-overlay">
      <div class="page-lock-box">
        <div class="page-lock-icon"><i class="fe fe-lock"></i></div>
        <h5 class="mb-1">Page Locked</h5>
        <p class="mb-3 text-muted">
          <strong>{{ pageLock.lockedByName }}</strong> is currently reviewing this authorization.
          Please check back later.
        </p>
        <small class="text-muted">Auto-refreshes every 30 seconds</small>
      </div>
    </div>

    <main class="admin-main">

      <!-- ── Page Header ────────────────────────────────────────── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0; gap: 16px;">
            <div class="header-icon-wrap">
              <i class="fe fe-file-text" style="font-size: 22px;"></i>
            </div>
            <div class="flex-grow-1">
              <h4 class="mb-0 font-weight-bold">Authorization Code Details</h4>
              <div class="d-flex align-items-center mt-1" style="gap: 8px;" v-if="auth_code">
                <span :class="`badge badge-soft-${auth_code.color} px-3 py-1`"
                  style="border-radius: 20px; text-transform: capitalize; font-size: .8rem;">
                  <i class="fe fe-activity mr-1"></i> {{ auth_code.status }}
                  <span v-if="auth_code.code_usage" class="mx-1">&middot;</span> {{ auth_code.code_usage }}
                </span>
                <span v-if="auth_code.code_created" class="badge badge-soft-info px-3 py-1"
                  style="border-radius: 20px; font-size: .8rem;">
                  <i class="fe fe-key mr-1"></i> {{ auth_code.code_created }}
                </span>
              </div>
            </div>

            <!-- ── Lock status ── -->
            <div class="lock-pill lock-mine" v-if="pageLock.isOwner">
              <span class="lock-dot"></span>
              <i class="fe fe-lock" style="font-size: 12px;"></i>
              <span>Locked by you</span>
            </div>
            <div class="lock-pill lock-other" v-else-if="pageLock.locked && pageLock.lockedByName">
              <i class="fe fe-eye mr-1" style="font-size: 12px;"></i>
              <span>{{ pageLock.lockedByName }} is viewing</span>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3" :class="queueIds.length ? 'pb-queue' : 'pb-5'">

        <!-- ── Action Bar ─────────────────────────────────────────── -->
        <div class="card action-card shadow mb-4">
          <div class="card-body py-3 px-4 d-flex flex-wrap align-items-center" style="gap: 8px;">


            <button class="btn btn-sm btn-outline-secondary" @click="printMe">
              <i class="fe fe-printer mr-1"></i> Print
            </button>

            <router-link :to="`/encounter/${auth_code.encounter.id}`" v-if="auth_code && auth_code.encounter != null">
              <button type="button" class="btn btn-sm btn-outline-info">
                <i class="fe fe-eye mr-1"></i> View Encounter
              </button>
            </router-link>

            <button data-toggle="modal" data-target="#slideRightModalAll" class="btn btn-sm btn-outline-info">
              <i class="fe fe-thermometer mr-1"></i> Services
            </button>

            <button v-if="auth_code && auth_code.enrollee" class="btn btn-sm btn-outline-success"
              @click="$router.push({ path: '/add-record', query: { enrollee: auth_code.enrollee.id_card_number, type: 'secondary', reason: 'Referred In' } })">
              <i class="fe fe-file-plus mr-1"></i> Claim
            </button>

            <button v-if="auth_code" class="btn btn-sm btn-outline-primary" @click="openTransferModal">
              <i class="fe fe-repeat mr-1"></i> {{ isAgencyStaff ? "Change receiving facility" : "Transfer" }}
            </button>

            <button class="btn btn-sm btn-outline-dark" @click="inProgress"
              v-if="auth_code && auth_code.is_corridor == true">
              <i class="fe fe-navigation mr-1"></i> Out-of-State Care
            </button>

            <div class="ml-auto d-flex flex-wrap" style="gap: 6px;">
              <button class="btn btn-sm btn-outline-secondary" v-if="auth_code && (isAgencyStaff || (user && user.type === 'nhia'))" @click="activityOpen = true">
                <i class="fe fe-clock mr-1"></i> Activity log
              </button>
              <button class="btn btn-sm btn-outline-info" @click="fetchAuthCodeSummary"
                v-if="user && (user.user_role == '1' || user.job_title == 'Claims Verifier')" data-toggle="modal"
                data-target="#authCodeSummaryModal">
                <i class="fe fe-bar-chart-2 mr-1"></i> Summary Review
              </button>

              <button class="btn btn-sm btn-outline-secondary" v-if="auth_code && auth_code.code_created != null"
                @click="copyCode">
                <i class="fe fe-copy mr-1"></i> Copy Code
              </button>

              <button v-if="user && (user.user_role == 1 || user.job_title == 'Claims Verifier')"
                class="btn btn-sm btn-outline-secondary" data-toggle="modal" data-target="#example_01">
                <i class="fe fe-calendar mr-1"></i> Update Expiry
              </button>

              <button class="btn btn-sm btn-success" @click="runCheckerForCode" v-if="canGenerateAuthCode">
                <i class="fe fe-lock mr-1"></i> Generate Auth Code
              </button>

              <button class="btn btn-sm btn-danger" @click="rejectRef" v-if="canRejectAuthCode">
                <i class="fe fe-x mr-1"></i> Reject
              </button>

              <button class="btn btn-sm btn-success" @click="resubmitRef" v-if="
                  auth_code &&
                  auth_code.status == 'rejected' &&
                  user && user.type == 'provider_employee'
                ">
                <i class="fe fe-refresh-cw mr-1"></i> Resubmit
              </button>
            </div>
          </div>
        </div>

        <!-- ── Request Timeline ───────────────────────────────────── -->
        <div class="card shadow table-card mb-4" v-if="auth_code">
          <div class="card-body py-3">
            <div class="h-timeline">

              <!-- Created -->
              <div class="h-timeline-step">
                <div class="h-timeline-icon bg-primary">
                  <i class="fe fe-file-plus"></i>
                </div>
                <div class="h-timeline-label">
                  <strong>Created</strong>
                  <span>{{ auth_code.creator ? auth_code.creator.full_name : '—' }}</span>
                  <span>{{ auth_code.created_at | moment("MMM Do, h:mm a") }}</span>
                </div>
              </div>

              <div class="h-timeline-connector" :class="{ 'h-timeline-connector--done': auth_code.treated_at }"></div>

              <!-- Treated (approved / rejected) -->
              <div class="h-timeline-step">
                <div class="h-timeline-icon"
                  :class="!auth_code.treated_at ? 'bg-warning' : (auth_code.status === 'rejected' ? 'bg-danger' : 'bg-success')">
                  <i v-if="!auth_code.treated_at" class="fe fe-clock"></i>
                  <i v-else-if="auth_code.status === 'rejected'" class="mdi mdi-close-circle-outline"></i>
                  <i v-else class="mdi mdi-check-all"></i>
                </div>
                <div class="h-timeline-label">
                  <strong>{{ !auth_code.treated_at ? 'Awaiting Decision' : (auth_code.status === 'rejected' ? 'Rejected' : 'Approved') }}</strong>
                  <template v-if="auth_code.treated_at">
                    <span>{{ auth_code.staff ? auth_code.staff.full_name : '—' }}</span>
                    <span>
                      {{ auth_code.treated_at | moment("MMM Do, h:mm a") }}
                      <template v-if="auth_code.treatment_time">&middot; {{ auth_code.treatment_time }}</template>
                    </span>
                  </template>
                </div>
              </div>

              <div class="h-timeline-connector" :class="{ 'h-timeline-connector--done': auth_code.claim }"></div>

              <!-- Claimed -->
              <div class="h-timeline-step" :class="{ 'h-timeline-step--muted': !auth_code.claim }">
                <div class="h-timeline-icon" :class="auth_code.claim ? 'bg-info' : 'h-timeline-icon--muted'">
                  <i class="fe fe-file-text"></i>
                </div>
                <div class="h-timeline-label">
                  <strong>Claimed</strong>
                  <template v-if="auth_code.claim">
                    <router-link :to="`/claim/${auth_code.claim.id}`" target="_blank">
                      {{ auth_code.claim.claim_unique_id }}
                    </router-link>
                    <span>
                      &#8358;{{ auth_code.claim.requested_total_cost | numeral(0, 0) }}
                      <template v-if="auth_code.claim.claim_status"> &middot; {{ auth_code.claim.claim_status }}</template>
                    </span>
                  </template>
                  <span v-else>Not yet claimed</span>
                </div>
              </div>

              <div class="h-timeline-connector"
                :class="{ 'h-timeline-connector--done': auth_code.claim && claimStatus.key !== 'pending' }"></div>

              <!-- Claim Treated (vetted / verified / approved) -->
              <div class="h-timeline-step" :class="{ 'h-timeline-step--muted': !auth_code.claim }">
                <div class="h-timeline-icon" :class="auth_code.claim ? claimStatusIconClass : 'h-timeline-icon--muted'">
                  <i :class="auth_code.claim ? ['fe', claimStatus.icon] : 'fe fe-check-square'"></i>
                </div>
                <div class="h-timeline-label">
                  <strong>{{ auth_code.claim ? claimStatus.label : 'Claim Treated' }}</strong>
                  <template v-if="auth_code.claim">
                    <span v-if="auth_code.claim.checked_by_date">Vetted {{ auth_code.claim.checked_by_date }}</span>
                    <span v-if="auth_code.claim.verified_by_date">Verified {{ auth_code.claim.verified_by_date }}</span>
                    <span v-if="auth_code.claim.approved_by_date">
                      Approved &#8358;{{ auth_code.claim.approved_total_cost | numeral(0, 0) }}
                    </span>
                  </template>
                  <span v-else>Awaiting a claim</span>
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- ── Comment Form ────────────────────────────────────────── -->
        <div class="card shadow mb-4" v-show="show">
          <div class="card-body">
            <label class="font-weight-bold text-secondary small">Leave a Comment</label>
            <textarea rows="4" class="form-control mb-3" v-model="body"></textarea>
            <button type="button" class="btn btn-submit px-4" @click="addComment">
              <i class="fe fe-send mr-1"></i> Submit
            </button>
          </div>
        </div>

        <!-- ── Info Cards ─────────────────────────────────────────── -->
        <div id="printDiv" ref="printNow">
          <div class="row mb-4" v-if="auth_code">
            <!-- Enrollee Information -->
            <div class="col-md-6 mb-4 mb-md-0">
              <div class="card shadow table-card h-100">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-user" style="font-size: 12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Enrollee Information</h6>
                </div>
                <!-- Enrollee photo -->
                <div v-if="auth_code.enrollee && auth_code.enrollee.user_image" class="text-center py-3"
                  style="border-bottom: 1px solid #f1f5f9;">
                  <router-link :to="patientLink(auth_code.enrollee)" title="Open patient page">
                    <img :src="auth_code.enrollee.image_url" class="rounded-circle"
                      style="width: 80px; height: 80px; object-fit: cover; border: 3px solid #2563eb; box-shadow: 0 4px 12px rgba(37,99,235,0.15);"
                      onerror="this.onerror=null; this.src='/assets/img/placeholder.svg'" />
                  </router-link>
                </div>

                <ul class="list-unstyled mb-0">
                  <li class="info-row" v-if="auth_code.enrollee != null">
                    <i class="fe fe-user info-icon"></i>
                    <div>
                      <p class="info-label">Full Name</p>
                      <router-link :to="patientLink(auth_code.enrollee)">
                        <p class="info-value mb-0">{{ auth_code.enrollee.full_name }}</p>
                      </router-link>
                    </div>
                  </li>
                  <li class="info-row" v-if="auth_code.enrollee != null">
                    <i class="fe fe-phone info-icon"></i>
                    <div>
                      <p class="info-label">Phone Number</p>
                      <p class="info-value">{{ auth_code.enrollee.phone_number }}</p>
                    </div>
                  </li>
                  <li class="info-row" v-if="auth_code.enrollee != null">
                    <i class="fe fe-hash info-icon"></i>
                    <div>
                      <p class="info-label">Insurance ID</p>
                      <p class="info-value">{{ auth_code.enrollee.id_card_number }}</p>
                    </div>
                  </li>

                  <li class="info-row" v-if="auth_code.enrollee != null">
                    <i class="fe fe-users info-icon"></i>
                    <div>
                      <p class="info-label">Gender</p>
                      <p class="info-value">{{ auth_code.enrollee.gender }}</p>
                    </div>
                  </li>
                  <li class="info-row" v-if="auth_code.enrollee != null">
                    <i class="fe fe-calendar info-icon"></i>
                    <div>
                      <p class="info-label">Date of Birth</p>
                      <p class="info-value">{{ auth_code.enrollee.dob }}</p>
                    </div>
                  </li>
                  <li class="info-row" v-if="auth_code.provider != null">
                    <i class="fe fe-home info-icon"></i>
                    <div>
                      <p class="info-label">Primary / Referring Facility</p>
                      <p class="info-value">{{ auth_code.provider.agency_name }}</p>
                    </div>
                  </li>
                  <li class="info-row">
                    <i class="fe fe-shuffle info-icon"></i>
                    <div>
                      <p class="info-label">Referral Type</p>
                      <p class="info-value">
                        <span v-if="auth_code.provider_id === auth_code.referred_to_facility">Intra-Referred Case</span>
                        <span v-else>Non-Intra Referral</span>
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Request Details -->
            <div class="col-md-6">
              <div class="card shadow table-card h-100">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-file-text" style="font-size: 12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Request Details</h6>
                </div>
                <ul class="list-unstyled mb-0">
                  <li class="info-row" v-if="auth_code.recipientfacility != null">
                    <i class="fe fe-map-pin info-icon"></i>
                    <div>
                      <p class="info-label">Secondary Facility</p>
                      <p class="info-value">{{ auth_code.recipientfacility.agency_name }}</p>
                    </div>
                  </li>
                  <li class="info-row">
                    <i class="fe fe-alert-circle info-icon"></i>
                    <div>
                      <p class="info-label">Encounter (Primary) Diagnosis</p>
                      <p class="info-value">
                        <span v-if="auth_code.encounter != null">
                          <span v-if="auth_code.encounter.diagnosis.diagnosis_code" class="text-muted font-weight-normal mr-1">({{ auth_code.encounter.diagnosis.diagnosis_code }})</span>{{ auth_code.encounter.diagnosis.name }}
                        </span>
                        <span v-else>—</span>
                      </p>
                    </div>
                  </li>

                  <li class="info-row">
                    <i class="fe fe-alert-circle info-icon"></i>
                    <div>
                      <p class="info-label">Referral Diagnosis</p>
                      <p class="info-value">
                        <span v-if="auth_code.diagnosis">
                          <span v-if="auth_code.diagnosis.diagnosis_code" class="text-muted font-weight-normal mr-1">({{ auth_code.diagnosis.diagnosis_code }})</span>{{ auth_code.diagnosis.name }}
                        </span>
                        <span v-else>—</span>
                      </p>
                    </div>
                  </li>

                  <li class="info-row">
                    <i class="fe fe-user-check info-icon"></i>
                    <div>
                      <p class="info-label">Prepared By</p>
                      <p class="info-value">{{ auth_code.creator.full_name }}</p>
                    </div>
                  </li>
                  <li class="info-row">
                    <i class="fe fe-clock info-icon"></i>
                    <div>
                      <p class="info-label">Requested At</p>
                      <p class="info-value mb-0">{{ auth_code.created_at | moment("ddd, MMM Do YYYY") }}</p>
                      <p class="mb-0 text-secondary" style="font-size: .8rem;">
                        {{ auth_code.created_at | moment("h:mm a") }}
                        <span :class="`ml-1 text-${auth_code.delay.color}`">
                          {{ auth_code.created_at | moment("from", "now") }}
                        </span>
                      </p>
                    </div>
                  </li>
                  <li class="info-row" v-if="auth_code.treatment_time">
                    <i class="fe fe-zap info-icon"></i>
                    <div>
                      <p class="info-label">Time to Decision</p>
                      <span class="badge badge-soft-info px-3 py-1" style="border-radius: 20px;">
                        {{ auth_code.treatment_time }}
                      </span>
                    </div>
                  </li>
                  <li class="info-row">
                    <i class="fe fe-calendar info-icon"></i>
                    <div>
                      <p class="info-label">Expiry Date</p>
                      <p class="info-value">{{ auth_code.expiry_date | moment("ddd, MMM Do YYYY") }}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Edit Referral (rejected, provider_employee) ───────────── -->
        <div class="card shadow table-card mb-4"
          v-if="auth_code && auth_code.status === 'rejected' && user && user.type === 'provider_employee'">
          <div class="form-card-header">
            <div class="section-number"><i class="fe fe-edit-2" style="font-size: 12px;"></i></div>
            <h6 class="mb-0 font-weight-bold text-white">Edit Referral</h6>
          </div>
          <div class="card-body">
            <div class="alert alert-info d-flex align-items-center mb-3" style="border-radius: 8px;">
              <i class="fe fe-info mr-2"></i>
              <span>This request was <strong>rejected</strong>. Update the diagnosis, receiving facility and/or
                services (via the Services button above), then click <strong>Resubmit</strong> to send it back for
                review.</span>
            </div>
            <div class="row">
              <div class="form-group col-md-6">
                <label class="small font-weight-bold text-secondary">Receiving Facility</label>
                <v-select v-model="editReferral.referred_to_facility" label="agency_name" :options="providers"
                  placeholder="Search receiving facility…" />
              </div>
              <div class="form-group col-md-6">
                <label class="small font-weight-bold text-secondary">Referral Diagnosis</label>
                <v-select v-model="editReferral.diagnosis_id" label="name" :options="diseases" :filterable="false"
                  @search="onDiagnosisSearch" placeholder="ICD 11 Diagnosis…">
                  <template #option="option">
                    <span class="text-muted mr-1">({{ option.diagnosis_code }})</span>{{ option.name }}
                  </template>
                  <template #selected-option="option">
                    ({{ option.diagnosis_code }}) - {{ option.name }}
                  </template>
                  <template #no-options="{ search }">
                    <span class="text-muted small">{{ search.length >= 4 ? 'No results found' : 'Search ICD 11 Diagnoses…' }}</span>
                  </template>
                </v-select>
              </div>
            </div>
            <button class="btn btn-submit px-4" :disabled="editReferral.submitting" @click="updateReferral">
              <span v-if="editReferral.submitting">
                <span class="spinner-border spinner-border-sm mr-1"></span> Saving…
              </span>
              <span v-else><i class="fe fe-save mr-1"></i> Save Changes</span>
            </button>
          </div>
        </div>

        <!-- ── Approved Services ─────────────────────────────────────── -->
        <div class="card shadow table-card mb-4" v-if="auth_code && auth_code.services && auth_code.services.length">
          <div class="form-card-header">
            <div class="section-number"><i class="fe fe-activity" style="font-size: 12px;"></i></div>
            <h6 class="mb-0 font-weight-bold text-white">Approved Services</h6>
            <span class="badge badge-light ml-auto" style="color: #1e3a5f;">
              {{ auth_code.services.length }} service(s)
            </span>
          </div>
          <div class="table-responsive">
            <table class="table mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th">#</th>
                  <th class="table-th">Service</th>
                  <th class="table-th">Frequency</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(svc, index) in auth_code.services" :key="svc.id" class="table-row">
                  <td class="py-2 px-4">{{ index + 1 }}</td>
                  <td class="py-2">
                    <span v-if="svc.service">{{ svc.service.description }}</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="py-2">{{ svc.frequency || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── Referral chain (re-referrals, who decided each step) ── -->
        <ReferralChain :code-id="$route.params.id" :key="'chain-' + $route.params.id" />

        <!-- ── Authorization Code History ──────────────────────────── -->
        <div class="card shadow table-card mb-4" v-if="referrals && referrals.data">
          <div class="d-flex align-items-center px-4 pt-4 pb-3">
            <i class="fe fe-clock text-primary mr-2"></i>
            <h6 class="mb-0 font-weight-bold">Authorization Code History</h6>
          </div>
          <div class="card-body pt-0">
            <div class="timeline">
              <div class="timeline-item" v-for="ref in referrals.data" :key="ref.id">
                <div class="timeline-wrapper">
                  <div>
                    <div class="avatar avatar-sm" v-if="ref.status == 'in-progress'">
                      <div class="avatar-title bg-primary rounded-circle">
                        <i class="mdi mdi-magnet"></i>
                      </div>
                    </div>
                    <div class="avatar avatar-sm" v-if="ref.status == 'rejected'">
                      <div class="avatar-title bg-danger rounded-circle">
                        <i class="mdi mdi-alert-circle-outline"></i>
                      </div>
                    </div>
                    <div class="avatar avatar-sm" v-if="ref.status == 'pending'">
                      <div class="avatar-title bg-warning rounded-circle">
                        <i class="fe fe-book-open"></i>
                      </div>
                    </div>
                    <div class="avatar avatar-sm" v-if="ref.status == 'approved'">
                      <div class="avatar-title bg-info rounded-circle">
                        <i class="mdi mdi-check-all"></i>
                      </div>
                    </div>
                  </div>
                  <div class="col-auto">
                    <h6 class="m-0" v-if="ref.encounter != null">
                      <router-link :to="`/authorization-code/${ref.id}`">
                        {{ ref.encounter.healthrecord.encounter_id }}
                        ({{ ref.encounter.diagnosis.name }})
                      </router-link>
                    </h6>
                    <span v-if="ref.id == $route.params.id" class="badge badge-soft-default mx-1">
                      <i class="fe fe-droplet"></i>
                    </span>
                    <span v-if="ref.code_created != null" class="badge badge-soft-info mx-1">
                      {{ ref.code_created }}
                    </span>
                    <a :href="`/authorization-code/${ref.id}`" class="badge badge-soft-dark mx-1">
                      <i class="fe fe-eye"></i>
                    </a>
                    <div class="small text-muted mt-1" v-if="ref.provider || ref.recipientfacility">
                      <span v-if="ref.provider">
                        <i class="fe fe-log-out mr-1"></i>From: <strong>{{ ref.provider.agency_name }}</strong>
                      </span>
                      <span v-if="ref.provider && ref.recipientfacility" class="mx-1">&rarr;</span>
                      <span v-if="ref.recipientfacility">
                        <i class="fe fe-log-in mr-1"></i>To: <strong>{{ ref.recipientfacility.agency_name }}</strong>
                      </span>
                    </div>
                  </div>
                  <div class="ml-auto col-auto text-muted">
                    {{ ref.created_at | moment("from", "now") }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Comments ───────────────────────────────────────────── -->
        <div class="card shadow table-card mb-4">
          <div class="d-flex align-items-center px-4 pt-4 pb-3">
            <i class="fe fe-message-circle text-primary mr-2"></i>
            <h6 class="mb-0 font-weight-bold">Comments</h6>
          </div>
          <div class="card-body pt-0">
            <div v-if="auth_code && auth_code.comments && auth_code.comments.length">
              <div class="media mb-3 p-3"
                style="background: #f8f9fa; border-left: 3px solid #2563eb; border-radius: 6px;"
                v-for="comment in auth_code.comments" :key="comment.id">
                <div class="avatar avatar-sm mr-3">
                  <div class="avatar-title rounded-circle bg-soft-info text-info" style="font-size: .75rem;">
                    <i class="fe fe-user"></i>
                  </div>
                </div>
                <div class="media-body">
                  <p class="mb-1">{{ comment.body }}</p>
                  <small class="text-muted">
                    <span v-if="comment.user != null">
                      <strong>{{ comment.user.full_name }}</strong> &middot; {{ comment.user.type }}
                    </span>
                    &middot; {{ comment.created_at | moment("MMM Do YYYY, h:mm a") }}
                  </small>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-muted py-5">
              <i class="fe fe-message-circle" style="font-size: 2rem;"></i>
              <p class="mt-2 mb-0">No comments yet</p>
            </div>
          </div>
        </div>

      </div>

      <!-- ── Expiry Update Modal ────────────────────────────────── -->
      <div class="modal fade" id="example_01" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header" style="background: linear-gradient(135deg, #1e3a5f, #2563eb);">
              <h6 class="modal-title text-white font-weight-bold mb-0">
                <i class="fe fe-calendar mr-2"></i> Update Expiry Date
              </h6>
              <button type="button" class="close text-white ml-auto" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="modal-body p-4">
              <div class="form-group">
                <label class="small font-weight-bold text-secondary">New Expiry Date</label>
                <input type="date" class="form-control" v-model="auth_code.expiry_date" />
              </div>
              <button class="btn btn-submit btn-block mt-3" @click="updateExpiry">
                <i class="fe fe-check mr-1"></i> Submit
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Services Modal ─────────────────────────────────────── -->
      <div class="modal fade modal-slide-right" id="slideRightModalAll" tabindex="-1" role="dialog"
        aria-labelledby="slideRightModalLabel" aria-hidden="true">
        <div class="modal-dialog scrollmodal" role="document">
          <div class="modal-content">
            <div class="modal-header" style="background: linear-gradient(135deg, #1e3a5f, #2563eb);">
              <div class="d-flex align-items-center">
                <div class="header-icon-wrap mr-3" style="width:36px;height:36px;">
                  <i class="fe fe-thermometer" style="font-size:16px;color:#fff;"></i>
                </div>
                <h5 class="modal-title mb-0 text-white font-weight-bold" id="slideRightModalLabel">
                  Services for this Request
                </h5>
              </div>
              <button type="button" class="close text-white ml-auto" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div class="modal-body">
              <div v-if="auth_code && auth_code.status === 'approved'"
                class="alert alert-warning d-flex align-items-center mb-3" style="border-radius: 8px;">
                <i class="fe fe-lock mr-2"></i>
                <span>This code is <strong>approved</strong> — services are locked and cannot be modified.</span>
              </div>

              <div v-if="auth_code && auth_code.status === 'rejected' && user && user.type === 'provider_employee'"
                class="alert alert-info d-flex align-items-center mb-3" style="border-radius: 8px;">
                <i class="fe fe-edit-2 mr-2"></i>
                <span>This request was <strong>rejected</strong>. Adjust the services below, then click
                  <strong>Resubmit</strong> on the request page to send it back for review.</span>
              </div>

              <form @submit.prevent="addServiceRendered" v-if="auth_code && auth_code.status !== 'approved'">
                <div class="row">
                  <div class="form-group col-md-12">
                    <label class="small font-weight-bold text-secondary">Select Service</label>
                    <v-select v-model="service.service_id" label="description" :required="!service.service_id"
                      :options="filteredServiceOptions" />
                  </div>
                  <!-- <div class="form-group col-md-6">
                    <label class="small font-weight-bold text-secondary">Frequency</label>
                    <input type="number" min="1" class="form-control" required placeholder="freq"
                      v-model="service.frequency" />
                    <small class="text-muted">number of times in a day</small>
                  </div>
                  <div class="form-group col-md-6">
                    <label class="small font-weight-bold text-secondary">Duration (Days)</label>
                    <input type="number" min="1" max="50" class="form-control" required placeholder="Qty"
                      v-model="service.days" />
                    <small class="text-muted">number of days</small>
                  </div> -->
                </div>
                <button class="btn btn-submit btn-block mb-4">
                  <i class="fe fe-send mr-1"></i> Add Service
                </button>
              </form>

              <h6 class="font-weight-bold mb-3">
                <i class="fe fe-activity mr-1 text-primary"></i> Approved Services
              </h6>
              <div class="table-responsive">
                <table class="table mb-0">
                  <thead class="table-head">
                    <tr>
                      <th class="table-th">#</th>
                      <th class="table-th">Name</th>
                      <th class="table-th">Freq.</th>
                      <!-- <th class="table-th">Days</th> -->
                      <th class="table-th">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(svc, index) in auth_code.services" :key="svc.id" class="table-row">
                      <td class="py-2 px-3">{{ index + 1 }}</td>
                      <td class="py-2">
                        <span v-if="svc.service != null">{{ svc.service.description }}</span>
                      </td>
                      <td class="py-2">{{ svc.frequency }}</td>
                      <!-- <td class="py-2">{{ svc.days }}</td> -->
                      <td class="py-2">
                        <button
                          v-if="user && (user.type == 'employee' || user.type == 'shis' || user.type == 'provider_employee') && auth_code.status !== 'approved'"
                          class="btn btn-sm btn-danger" @click="deleteService(svc)">
                          <i class="fe fe-trash-2"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">
                <i class="fe fe-x mr-1"></i> Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Auth Code Summary Review Modal ────────────────────── -->
      <div class="modal fade modal-slide-right" id="authCodeSummaryModal" tabindex="-1" role="dialog"
        aria-hidden="true">
        <div class="modal-dialog scrollmodal" role="document" style="width: 520px; max-width: 100%;">
          <div class="modal-content">
            <div class="modal-header" style="background: linear-gradient(135deg, #1e3a5f, #2563eb);">
              <div class="d-flex align-items-center">
                <div class="header-icon-wrap mr-3" style="width:36px;height:36px;">
                  <i class="fe fe-bar-chart-2" style="font-size:16px;color:#fff;"></i>
                </div>
                <h5 class="modal-title mb-0 text-white font-weight-bold">Authorization Code Summary</h5>
              </div>
              <button type="button" class="close text-white ml-auto" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div class="modal-body">
              <div v-if="summaryLoading" class="text-center py-5">
                <div class="spinner-border text-info" role="status"></div>
                <p class="mt-2 text-muted">Analysing request…</p>
              </div>

              <div v-else-if="authCodeSummary">
                <div v-if="authCodeSummary.duplicate_check.is_potential_duplicate"
                  class="alert alert-danger d-flex align-items-start mb-3" role="alert">
                  <i class="fe fe-alert-triangle mr-2 mt-1"></i>
                  <div>
                    <strong>Duplicate / Conflict Detected</strong>
                    <ul class="mb-0 mt-1 pl-3">
                      <li v-for="(reason, i) in authCodeSummary.duplicate_check.reasons" :key="i">{{ reason }}</li>
                    </ul>
                  </div>
                </div>

                <div class="card mb-3" style="border-left: 4px solid #2563eb;">
                  <div class="card-body py-3">
                    <p class="mb-1 text-muted"
                      style="font-size: .78rem; text-transform: uppercase; letter-spacing: .05em;">
                      <i class="fe fe-file-text mr-1"></i> Summary
                    </p>
                    <p class="mb-0" style="line-height: 1.7;" v-html="parseMarkdown(authCodeSummary.narrative)"></p>
                  </div>
                </div>

                <div v-if="authCodeSummary.last_request" class="card mb-3 border-warning">
                  <div class="card-body py-3 d-flex align-items-start">
                    <i class="fe fe-clock text-warning mr-2 mt-1"></i>
                    <div>
                      <p class="mb-1 font-weight-bold" style="font-size: .85rem;">Last Request</p>
                      <p class="mb-0" style="font-size: .82rem;">
                        <span class="text-info">{{ authCodeSummary.last_request.time_ago }}</span>
                        <span class="text-muted mx-1">·</span>
                        {{ authCodeSummary.last_request.date_requested }}
                      </p>
                      <p class="mb-0 mt-1" style="font-size: .82rem;" v-if="authCodeSummary.last_request.diagnosis">
                        Diagnosis: <strong>{{ authCodeSummary.last_request.diagnosis }}</strong>
                      </p>
                      <p class="mb-0 mt-1" style="font-size: .82rem;"
                        v-if="authCodeSummary.last_request.referred_to_facility">
                        Facility: <strong>{{ authCodeSummary.last_request.referred_to_facility }}</strong>
                      </p>
                      <span
                        :class="`badge badge-soft-${authCodeSummary.last_request.status === 'approved' ? 'success' : authCodeSummary.last_request.status === 'rejected' ? 'danger' : 'warning'} mt-1`">
                        {{ authCodeSummary.last_request.status }}
                      </span>
                    </div>
                  </div>
                </div>
                <div v-else class="alert alert-light mb-3" style="font-size: .82rem;">
                  <i class="fe fe-info mr-1 text-muted"></i> This is the first authorization code request for this
                  enrollee.
                </div>

                <div class="row mb-3">
                  <div class="col-6">
                    <div class="card text-center py-3">
                      <p class="mb-1 text-muted" style="font-size: .72rem; text-transform: uppercase;">Requests (Past
                        Month)</p>
                      <h4 class="mb-0 text-info">{{ authCodeSummary.monthly_stats.total_codes_this_period }}</h4>
                    </div>
                  </div>
                  <div class="col-6">
                    <div class="card text-center py-3">
                      <p class="mb-1 text-muted" style="font-size: .72rem; text-transform: uppercase;">Approved /
                        Pending</p>
                      <h4 class="mb-0 text-info">
                        {{ authCodeSummary.monthly_stats.approved_count }} /
                        {{ authCodeSummary.monthly_stats.pending_count }}
                      </h4>
                    </div>
                  </div>
                </div>

                <div class="card mb-3" v-if="authCodeSummary.referral_breakdown.length">
                  <div class="card-header py-2">
                    <span class="font-weight-bold" style="font-size: .85rem;">
                      <i class="fe fe-map-pin text-info mr-1"></i> Referral Breakdown (Past Month)
                    </span>
                  </div>
                  <div class="table-responsive">
                    <table class="table table-sm mb-0">
                      <thead class="table-head">
                        <tr>
                          <th class="table-th" style="font-size: .78rem;">Facility</th>
                          <th class="table-th text-center" style="font-size: .78rem;">Referrals</th>
                          <th class="table-th" style="font-size: .78rem;">Last</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="r in authCodeSummary.referral_breakdown" :key="r.facility_id" class="table-row">
                          <td style="font-size: .82rem;">{{ r.facility_name }}</td>
                          <td class="text-center" style="font-size: .82rem;">{{ r.count }}</td>
                          <td style="font-size: .82rem;">{{ r.last_referral }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div v-if="authCodeSummary.duplicate_check.active_unused_codes.length" class="card mb-3">
                  <div class="card-header py-2">
                    <span class="font-weight-bold text-warning" style="font-size: .85rem;">
                      <i class="fe fe-unlock mr-1"></i> Active Unused Codes
                    </span>
                  </div>
                  <ul class="list-unstyled mb-0">
                    <li class="info-row" v-for="c in authCodeSummary.duplicate_check.active_unused_codes" :key="c.id">
                      <i class="fe fe-key info-icon text-info"></i>
                      <div>
                        <p class="info-value mb-0"><strong class="text-info">{{ c.code || '(pending)' }}</strong></p>
                        <p class="info-label mb-0">{{ c.referred_to_facility }} &middot; expires {{ c.expiry_date || '—'
                          }}</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              <div v-else class="text-center text-muted py-5">
                <i class="fe fe-bar-chart-2" style="font-size: 2rem;"></i>
                <p class="mt-2">Click "Summary Review" to analyse this request.</p>
              </div>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Transfer Modal ────────────────────────────────────── -->
      <div class="modal fade" id="transferModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content" style="border-radius: 12px; overflow: hidden; border: none;">
            <div class="modal-header"
              style="background: linear-gradient(135deg, #1e3a5f, #2563eb); border-bottom: none;">
              <div class="d-flex align-items-center">
                <div class="header-icon-wrap mr-3" style="width: 36px; height: 36px;">
                  <i class="fe fe-repeat" style="font-size: 16px; color: #fff;"></i>
                </div>
                <h5 class="modal-title mb-0 text-white font-weight-bold">{{ isAgencyStaff ? "Change receiving facility" : "Transfer Referral" }}</h5>
              </div>
              <button type="button" class="close text-white ml-auto" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div class="modal-body p-4">
              <!-- Enrollee preview -->
              <div v-if="auth_code && auth_code.enrollee" class="d-flex align-items-center p-3 rounded mb-4"
                style="background: #f0f4ff; border: 1px solid #d0d9ee;">
                <img v-if="auth_code.enrollee.user_image" :src="auth_code.enrollee.image_url"
                  class="rounded-circle mr-3"
                  style="width: 48px; height: 48px; object-fit: cover; border: 2px solid #2563eb; flex-shrink: 0;"
                  onerror="this.onerror=null; this.src='/assets/img/placeholder.svg'" />
                <div v-else class="rounded-circle mr-3 d-flex align-items-center justify-content-center bg-white"
                  style="width: 48px; height: 48px; min-width: 48px; border: 2px solid #2563eb;">
                  <i class="fe fe-user text-info"></i>
                </div>
                <div>
                  <p class="mb-0 font-weight-bold" style="font-size: 0.9rem;">{{ auth_code.enrollee.full_name }}</p>
                  <small class="text-muted">{{ auth_code.enrollee.id_card_number }}</small>
                </div>
              </div>

              <div class="form-group mb-0">
                <label class="small font-weight-bold text-secondary">
                  New Receiving Facility <span class="text-danger">*</span>
                </label>
                <v-select v-model="transferModal.recipientFacility" label="agency_name" :options="isAgencyStaff && transferModal.options.length ? transferModal.options : providers"
                  placeholder="Search facility…" />
                <small v-if="auth_code && auth_code.recipientfacility" class="text-muted d-block mt-1">Now: {{ auth_code.recipientfacility.agency_name }}</small>
              </div>
              <div class="form-group mb-0 mt-3" v-if="isAgencyStaff">
                <label class="small font-weight-bold text-secondary">Reason <span class="text-danger">*</span></label>
                <textarea class="form-control" rows="3" v-model.trim="transferModal.reason" maxlength="500"
                  placeholder="Why is the enrollee being moved? (kept in the activity log and sent to the facilities)"></textarea>
              </div>
            </div>

            <div class="modal-footer" style="border-top: 1px solid #f1f5f9;">
              <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">Cancel</button>
              <button type="button" class="btn btn-submit px-4" @click="submitTransfer"
                :disabled="transferModal.submitting || !transferModal.recipientFacility || (isAgencyStaff && transferModal.reason.length < 3)">
                <span v-if="transferModal.submitting">
                  <span class="spinner-border spinner-border-sm mr-1"></span> Transferring…
                </span>
                <span v-else><i class="fe fe-check mr-1"></i> Confirm Transfer</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Rejection Reasons Modal ───────────────────────────── -->
      <div class="modal fade" id="rejectionReasonsModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content" style="border-radius: 12px; overflow: hidden; border: none;">
            <div class="modal-header" style="background: linear-gradient(135deg, #7f1d1d, #ef4444); border-bottom: none;">
              <div class="d-flex align-items-center">
                <div class="header-icon-wrap mr-3" style="width:36px;height:36px;background:rgba(255,255,255,0.15);">
                  <i class="fe fe-x-circle" style="font-size:16px;color:#fff;"></i>
                </div>
                <div>
                  <h5 class="modal-title mb-0 text-white font-weight-bold">Reasons for Rejection</h5>
                  <small style="color:rgba(255,255,255,.75);">Select a reason — it will be added as a comment</small>
                </div>
              </div>
              <button type="button" class="close text-white ml-auto" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div class="modal-body p-4">
              <div class="rejection-reasons-list">
                <button
                  v-for="(reason, i) in rejectionReasons"
                  :key="i"
                  class="rejection-reason-btn"
                  :class="{ selected: rejectionModal.selectedReason === reason }"
                  @click="rejectionModal.selectedReason = reason"
                >
                  <span class="reason-num">{{ i + 1 }}</span>
                  <span class="reason-text">{{ reason }}</span>
                  <i class="fe fe-check reason-check ml-auto" v-if="rejectionModal.selectedReason === reason"></i>
                </button>
              </div>

              <div v-if="rejectionModal.selectedReason === 'Others'" class="mt-3">
                <label class="small font-weight-bold text-secondary">Please specify:</label>
                <textarea
                  rows="3"
                  class="form-control"
                  v-model="rejectionModal.customReason"
                  placeholder="Enter custom rejection reason…"
                ></textarea>
              </div>
            </div>

            <div class="modal-footer" style="border-top: 1px solid #f1f5f9;">
              <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">Cancel</button>
              <button
                type="button"
                class="btn btn-danger px-4"
                @click="submitRejection"
                :disabled="rejectionModal.submitting || !rejectionModal.selectedReason || (rejectionModal.selectedReason === 'Others' && !rejectionModal.customReason.trim())"
              >
                <span v-if="rejectionModal.submitting">
                  <span class="spinner-border spinner-border-sm mr-1"></span> Rejecting…
                </span>
                <span v-else><i class="fe fe-x mr-1"></i> Confirm Rejection</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Sticky Queue Navigation Bar ── -->
      <div class="queue-nav-bar" v-if="queueIds.length">
        <button class="btn btn-queue-nav" :disabled="!prevId" @click="goToQueue(prevId)">
          <i class="fe fe-chevron-left mr-1"></i> Prev
        </button>

        <div class="queue-nav-center">
          <span class="queue-nav-count">{{ queuePosition + 1 }} <span class="text-muted">of</span> {{ queueIds.length
            }}</span>
          <span class="queue-nav-label">Authorization Codes</span>
        </div>

        <button class="btn btn-queue-nav" :disabled="!nextId" @click="goToQueue(nextId)">
          Next <i class="fe fe-chevron-right ml-1"></i>
        </button>
      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>

      <ActivityLogPanel v-if="auth_code && auth_code.id" v-model="activityOpen" type="authorization_code" :record-id="auth_code.id"
        :subtitle="'Authorization code ' + (auth_code.code_created || '#' + auth_code.id) + ': who did what and when'" />
    </main>
  </div>
</template>

<script>
  import { marked } from "marked";
  import Navbar from "@/views/Navbar.vue";
  import ReferralChain from "@/views/reports/components/ReferralChain.vue";
  import ActivityLogPanel from "@/views/reports/components/ActivityLogPanel.vue";
  import Loading from "vue-loading-overlay";
  import "vue-loading-overlay/dist/vue-loading.css";
  import { isBhcpfContext, applyBhcpfFilter } from "@/utils/bhcpfFilter";
  import { getClaimStatus } from "@/utils/claimStatus";
  import { patientLink } from "@/utils/patientLink";
  let agency = JSON.parse(localStorage.getItem("agency"));
  const PAGE_LOCK_TTL_MS = 5 * 60 * 1000;

  export default {
    components: { ReferralChain, ActivityLogPanel,
      Navbar,
      Loading,
    },
    data() {
      return {
        user: null,
        services: [],
        service: {
          frequency: 1,
          days: 1,
        },
        auth_code: "",
        encounter: "",
        shownotes: false,
        facility: "",
        clientdetail: "",
        referrals: "",
        body: "",
        show: true,
        isLoading: false,
        fullPage: true,
        agency_id: "",
        authCodeSummary: null,
        summaryLoading: false,
        providers: [],
        transferModal: {
          recipientFacility: null,
          submitting: false,
          reason: "",
          options: [],
        },
        activityOpen: false,
        pageLock: {
          locked: false,
          isOwner: false,
          lockedByName: "",
        },
        lockHeartbeat: null,
        lockPoll: null,
        rejectionReasons: [
          "Duplicate request",
          "Service not covered",
          "Diagnosis not covered",
          "Service covered at primary care / capitated",
          "Wrong selection of ICD-11 diagnosis",
          "Wrong diagnosis for gender",
          "Facility you are referring to is not accredited for the service",
          "Others",
        ],
        rejectionModal: {
          selectedReason: null,
          customReason: "",
          submitting: false,
        },
        editReferral: {
          diagnosis_id: null,
          referred_to_facility: null,
          submitting: false,
        },
        diseases: [],
        diagnosisSearchTimeout: null,
      };
    },
    beforeMount() {
      this.user = JSON.parse(localStorage.getItem("user"));
    },
    beforeDestroy() {
      this.releaseLock();
      clearInterval(this.lockHeartbeat);
      clearInterval(this.lockPoll);
    },
    watch: {
      '$route.params.id'(newId, oldId) {
        if (newId !== oldId) {
          this.auth_code = "";
          this.referrals = "";
          this.getCode();
          this.initPageLock();
        }
      },
    },

    computed: {
      // agency staff move enrollees between receiving facilities (logged with a reason) and see the activity log
      isAgencyStaff() {
        return !!(this.user && ["shis", "employee"].includes(this.user.type));
      },
      filteredServiceOptions() {
        const list = (this.services && this.services.data) ? this.services.data : [];
        const enrollee = this.auth_code && this.auth_code.enrollee;
        return applyBhcpfFilter(list, isBhcpfContext(agency.id, enrollee));
      },

      claimStatus() {
        return getClaimStatus(this.auth_code ? this.auth_code.claim : null);
      },
      claimStatusIconClass() {
        const map = {
          success: "bg-success",
          danger: "bg-danger",
          warning: "bg-warning",
          info: "bg-info",
          dark: "bg-dark",
          indigo: "h-timeline-icon--indigo",
          forwarded: "bg-primary",
        };
        return map[this.claimStatus.variant] || "bg-secondary";
      },

      queueIds() {
        const q = this.$route.query.queue;
        return q ? q.split(',').map(Number) : [];
      },
      lockedSet() {
        const l = this.$route.query.locked;
        return l ? new Set(l.split(',').map(Number)) : new Set();
      },
      queuePosition() {
        return this.queueIds.indexOf(Number(this.$route.params.id));
      },
      nextId() {
        const ids = this.queueIds;
        for (let i = this.queuePosition + 1; i < ids.length; i++) {
          if (!this.lockedSet.has(ids[i])) return ids[i];
        }
        return null;
      },
      prevId() {
        const ids = this.queueIds;
        for (let i = this.queuePosition - 1; i >= 0; i--) {
          if (!this.lockedSet.has(ids[i])) return ids[i];
        }
        return null;
      },

      isAuthCodeApprover() {
        return this.user && (
          this.user.job_title == 'Claims Verifier' ||
          this.user.job_title == 'Authorization Code' ||
          this.user.user_role == '1'
        );
      },
      canGenerateAuthCode() {
        return !!(
          this.auth_code &&
          this.auth_code.status != 'approved' &&
          (this.isAuthCodeApprover || (this.user && this.user.type == 'tpa_employee'))
        );
      },
      canRejectAuthCode() {
        return !!(
          this.auth_code &&
          this.auth_code.status != 'rejected' &&
          this.isAuthCodeApprover
        );
      },
    },

    methods: {

      patientLink,
      goToQueue(id) {
        this.releaseLock();
        this.$router.push({
          path: `/authorization-code/${id}`,
          query: this.$route.query,
        });
      },

      getCode() {
        this.isLoading = true;
        this.user = JSON.parse(localStorage.getItem("user"));
        this.axios
          .get(`/api/v1/auth/single_authorization_code/${this.$route.params.id}`)
          .then((response) => {
            this.auth_code = response.data;
            this.editReferral.referred_to_facility = this.auth_code.recipientfacility || null;
            this.editReferral.diagnosis_id = this.auth_code.diagnosis || null;
            let patient_id = this.auth_code.enrollee.id;
            this.getReferralPatient(patient_id);
            console.log(response);
            this.isLoading = false;
          })
          .catch((error) => {
            console.error(error);
            this.isLoading = true;
          });
      },
      getReferralPatient(patient_id) {
        this.isLoading = true;
        this.axios
          .get(`/api/v1/auth/referrals-patient/${patient_id}`, {
            params: {
              user_type: this.auth_code.enrollee.account_type,
            },
          })
          .then((response) => {
            console.log(response);
            this.referrals = response.data;
            this.isLoading = false;
          })
          .catch((error) => {
            console.log(error.response);
            this.isLoading = false;
          });
      },

      addComment() {
        this.axios
          .post(`/api/v1/auth/addComment/referral`, {
            authorization_code_id: this.$route.params.id,
            body: this.body,
          })
          .then((response) => {
            console.log(response);
            this.getCode();
            this.show = false;
            this.$toasted.info("added Successfully!", {
              position: "top-center",
              duration: 3000,
            });
          });
      },

      rejectRef() {
        this.rejectionModal.selectedReason = null;
        this.rejectionModal.customReason = "";
        this.rejectionModal.submitting = false;
        // eslint-disable-next-line no-undef
        $('#rejectionReasonsModal').modal('show');
      },

      submitRejection() {
        const reason = this.rejectionModal.selectedReason === 'Others'
          ? this.rejectionModal.customReason.trim()
          : this.rejectionModal.selectedReason;
        if (!reason) return;

        this.rejectionModal.submitting = true;
        this.axios
          .post(`/api/v1/auth/updateCodeRequestStatus`, {
            id: this.auth_code.id,
            status: "rejected",
          })
          .then(() => {
            return this.axios.post(`/api/v1/auth/addComment/referral`, {
              authorization_code_id: this.$route.params.id,
              body: reason,
            });
          })
          .then(() => {
            this.rejectionModal.submitting = false;
            // eslint-disable-next-line no-undef
            $('#rejectionReasonsModal').modal('hide');
            this.show = true;
            this.getCode();
            this.$toasted.info("Rejected Successfully!", { position: "top-center", duration: 3000 });
          })
          .catch((error) => {
            console.error(error);
            this.rejectionModal.submitting = false;
          });
      },

      resubmitRef() {
        if (confirm("Are you sure you want to resubmit this request for review?")) {
          this.isLoading = true;
          this.axios
            .post(`/api/v1/auth/updateCodeRequestStatus`, {
              id: this.auth_code.id,
              status: "pending",
            })
            .then(() => {
              this.isLoading = false;
              this.getCode();
              this.$toasted.success("Resubmitted for review!", {
                position: "top-center",
                duration: 3000,
              });
            })
            .catch((error) => {
              console.error(error);
              this.isLoading = false;
              this.$toasted.error("Failed to resubmit request.", {
                position: "top-center",
                duration: 3000,
              });
            });
        }
      },

      inProgress() {
        if (confirm("Are you sure you want to update this request?")) {
          this.axios
            .post(`/api/v1/auth/updateCodeRequestStatus`, {
              id: this.auth_code.id,
              status: "in-progress",
            })
            .then((response) => {
              console.log(response);
              this.getCode();
              this.$toasted.info("Updated Successfully!", {
                position: "top-center",
                duration: 3000,
              });
            });
        }
      },

      runCheckerForCode() {
        this.generateCode();
      },

      generateCode() {
        if (confirm("Are you sure you want to Approve?")) {
          this.axios
            .post(`/api/v1/auth/generateCode`, {
              id: this.auth_code.id,
              requested_by: this.auth_code.requested_by,
              expiry_days: 30,
            })
            .then((response) => {
              console.log(response);
              this.getCode();
              this.$toasted.info("Generated Successfully", { position: "top-center", duration: 3000 });
            })
            .catch((error) => {
              console.error(error);
            });
        }
      },
      verifyRef() {
        if (confirm("Are you sure you want to generate authorization code to approve?")) {
          this.axios
            .post(`/api/v1/auth/generate-token/${this.$route.params.id}`, {
              type: "approval",
            })
            .then((response) => {
              console.log(response);
              this.getCode();
              this.$router.push(`/all-referrals`);
              this.$toasted.info("Approved Successfully!", {
                position: "top-center",
                duration: 3000,
              });
            });
        }
      },

      copyCode() {
        const copyToClipboard = (text) => navigator.clipboard.writeText(text);
        copyToClipboard(this.auth_code.code_created);
        this.$toasted.info("Copied to clipboard", {
          position: "top-center",
          duration: 3000,
        });
      },
      printMe() {
        var printContents = document.getElementById("printDiv").innerHTML;
        var originalContents = document.body.innerHTML;
        document.body.innerHTML = printContents;
        window.print();
        document.body.innerHTML = originalContents;
      },

      updateExpiry() {
        if (confirm("Are you sure you want to Update Expiry Date?")) {
          this.axios
            .post(`/api/v1/auth/updateExpiry`, {
              id: this.$route.params.id,
              expiry_date: this.auth_code.expiry_date,
              referred_to_facility: this.auth_code.referred_to_facility,
            })
            .then((response) => {
              console.log(response);
              this.getCode();
              this.$toasted.info("Updated Successfully!", {
                position: "top-center",
                duration: 3000,
              });
            });
        }
      },

      getServices() {
        this.axios
          .get(`/api/v1/auth/service-agency/${agency.id}`)
          .then((response) => {
            this.services = response.data;
            console.log(response);
          })
          .catch((error) => {
            console.error(error);
          });
      },
      addServiceRendered() {
        this.user = JSON.parse(localStorage.getItem("user"));
        this.isLoading = true;
        this.axios
          .post("/api/v1/auth/authorization_code/add_service", {
            service_id: this.service.service_id.id,
            user_id: this.user.id,
            authorizationcode_id: this.$route.params.id,
            dose: this.service.dose,
            frequency: this.service.frequency,
            days: this.service.days,
          })
          .then((response) => {
            console.log(response);
            this.getCode();
            this.isLoading = false;
            this.$breadstick.notify("Added Successfully!", {
              position: "top-right",
            });
          })
          .catch((error) => {
            console.log(error.response);
          });
      },
      fetchAuthCodeSummary() {
        this.summaryLoading = true;
        this.authCodeSummary = null;
        this.axios
          .get(`/api/v1/auth/authorization-codes/${this.$route.params.id}/summary-review`)
          .then((response) => {
            this.authCodeSummary = response.data.data;
            this.summaryLoading = false;
          })
          .catch((error) => {
            console.error(error);
            this.summaryLoading = false;
            this.$toasted.error("Failed to load summary.", {
              position: "top-center",
              duration: 3000,
            });
          });
      },

      parseMarkdown(text) {
        return text ? marked(text) : "";
      },

      getProviders() {
        this.axios
          .get(`/api/v1/auth/providerAgency/${agency.id}`)
          .then((response) => { this.providers = response.data.data; })
          .catch((error) => { console.error(error); });
      },

      openTransferModal() {
        this.transferModal.recipientFacility = null;
        this.transferModal.submitting = false;
        this.transferModal.reason = "";
        if (this.isAgencyStaff) {
          // the receiving agency's facilities (the other state's for a cross-state request)
          this.axios.get(`/api/v1/auth/authorization_code/${this.auth_code.id}/receiving-facilities`)
            .then((res) => { this.transferModal.options = res.data.data || []; })
            .catch(() => { this.transferModal.options = []; });
        }
        // eslint-disable-next-line no-undef
        $('#transferModal').modal('show');
      },

      submitTransfer() {
        const { recipientFacility } = this.transferModal;
        if (!recipientFacility) return;
        this.transferModal.submitting = true;
        if (this.isAgencyStaff) {
          // agency staff: logged with the reason, and the facilities are told in chat
          this.axios.post(`/api/v1/auth/authorization_code/${this.auth_code.id}/receiving-facility`, { facility_id: recipientFacility.id, reason: this.transferModal.reason })
            .then((res) => {
              // eslint-disable-next-line no-undef
              $('#transferModal').modal('hide');
              this.$toasted.success(res.data.message || 'Receiving facility changed.', { position: 'top-center', duration: 4000 });
              this.getCode();
            })
            .catch((e) => {
              const d = e.response && e.response.data;
              this.$toasted.error((d && (d.errors ? Object.values(d.errors).flat()[0] : d.message)) || 'Could not change the receiving facility.', { position: 'top-center', duration: 5000 });
            })
            .finally(() => { this.transferModal.submitting = false; });
          return;
        }
        this.axios
          .post('/api/v1/auth/updateExpiry', {
            id: this.auth_code.id,
            expiry_date: this.auth_code.expiry_date,
            referred_to_facility: recipientFacility.id,
          })
          .then(() => {
            this.transferModal.submitting = false;
            // eslint-disable-next-line no-undef
            $('#transferModal').modal('hide');
            this.$toasted.success('Referral transferred successfully!', { position: 'top-center', duration: 3000 });
            this.getCode();
          })
          .catch((error) => {
            console.error(error);
            this.transferModal.submitting = false;
            this.$toasted.error('Failed to transfer referral.', { position: 'top-center', duration: 3000 });
          });
      },

      onDiagnosisSearch(query, loading) {
        clearTimeout(this.diagnosisSearchTimeout);
        if (query.trim().length < 4) {
          if (query.trim().length === 0) this.diseases = [];
          return;
        }
        loading(true);
        this.diagnosisSearchTimeout = setTimeout(() => {
          const q = query.trim().toLowerCase();
          this.axios
            .post(`/api/v1/auth/diagnosis-search`, { name: q })
            .then((response) => {
              const all = Array.isArray(response.data) ? response.data : (response.data.data || []);
              this.diseases = all.filter(d => d.name && d.name.toLowerCase().includes(q));
              loading(false);
            })
            .catch((error) => {
              console.error(error);
              loading(false);
            });
        }, 400);
      },

      updateReferral() {
        this.editReferral.submitting = true;
        this.axios
          .post(`/api/v1/auth/updateExpiry`, {
            id: this.auth_code.id,
            expiry_date: this.auth_code.expiry_date,
            referred_to_facility: this.editReferral.referred_to_facility ? this.editReferral.referred_to_facility.id : null,
            diagnosis_id: this.editReferral.diagnosis_id ? this.editReferral.diagnosis_id.id : null,
          })
          .then(() => {
            this.editReferral.submitting = false;
            this.getCode();
            this.$toasted.success("Referral updated successfully!", {
              position: "top-center",
              duration: 3000,
            });
          })
          .catch((error) => {
            console.error(error);
            this.editReferral.submitting = false;
            this.$toasted.error("Failed to update referral.", {
              position: "top-center",
              duration: 3000,
            });
          });
      },

      initPageLock() {
        const pageId = `auth-code-${this.$route.params.id}`;
        const isEmployee = this.user && this.user.type === 'employee';

        this.checkLock(pageId);

        if (isEmployee) {
          this.acquireLock(pageId);
          this.lockHeartbeat = setInterval(() => this.heartbeat(pageId), 30000);
        } else {
          this.lockPoll = setInterval(() => this.checkLock(pageId), 30000);
        }
      },

      checkLock(pageId) {
        this.axios
          .get(`/api/v1/auth/page-lock/${pageId}`)
          .then((res) => {
            const lock = res.data;
            const lockTimestamp = lock && (lock.updated_at || lock.locked_at || lock.created_at);
            const isStale = lockTimestamp
              ? (Date.now() - new Date(lockTimestamp).getTime()) > PAGE_LOCK_TTL_MS
              : false;
            if (lock && lock.user_id && !isStale) {
              this.pageLock.locked = true;
              this.pageLock.lockedByName = lock.user_name;
              this.pageLock.isOwner = this.user && this.user.id === lock.user_id;
            } else {
              this.pageLock.locked = false;
              this.pageLock.isOwner = false;
              this.pageLock.lockedByName = "";
            }
          })
          .catch(() => { });
      },

      acquireLock(pageId) {
        this.axios
          .post(`/api/v1/auth/page-lock`, {
            page_id: pageId,
            user_id: this.user.id,
            user_name: `${this.user.firstname} ${this.user.lastname}`,
          })
          .then(() => {
            this.pageLock.locked = true;
            this.pageLock.isOwner = true;
          })
          .catch(() => { });
      },

      heartbeat(pageId) {
        this.axios.put(`/api/v1/auth/page-lock/${pageId}`).catch(() => { });
      },

      releaseLock() {
        const pageId = `auth-code-${this.$route.params.id}`;
        const isEmployee = this.user && this.user.type === 'employee';
        if (isEmployee && this.pageLock.isOwner) {
          this.axios.delete(`/api/v1/auth/page-lock/${pageId}`).catch(() => { });
        }
      },

      deleteService(service) {
        if (confirm("Are you want you want to delete?")) {
          this.axios
            .delete(`/api/v1/auth/authorization_code/delete_service/${service.id}`)
            .then((response) => {
              console.log(response);
              this.getCode();
            })
            .catch((error) => {
              console.error(error);
            });
        }
      },
    },
    created() {
      this.getCode();
      this.getServices();
      this.getProviders();
      this.initPageLock();
    },
  };
</script>

<style scoped>
  .scrollmodal {
    height: 100%;
    margin: 0;
  }

  .scrollmodal .modal-content {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .scrollmodal .modal-body {
    overflow-y: auto;
    flex: 1;
  }

  /* ── Page header ── */
  .page-header {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  }

  /* ── Page Lock Overlay ── */
  .page-lock-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .page-lock-box {
    background: #fff;
    border-radius: 12px;
    padding: 40px 48px;
    text-align: center;
    max-width: 420px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  }

  .page-lock-icon {
    font-size: 2.5rem;
    color: #e67700;
    margin-bottom: 12px;
  }

  /* ── Lock pill (header) ── */
  .lock-pill {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 6px 14px 6px 10px;
    border-radius: 24px;
    font-size: .78rem;
    font-weight: 600;
    flex-shrink: 0;
    backdrop-filter: blur(6px);
  }

  .lock-mine {
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: #fff;
  }

  .lock-other {
    background: rgba(239, 68, 68, 0.18);
    border: 1px solid rgba(239, 68, 68, 0.4);
    color: #fff;
  }

  .lock-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #4ade80;
    flex-shrink: 0;
    animation: lock-pulse 1.8s ease-in-out infinite;
  }

  @keyframes lock-pulse {

    0%,
    100% {
      opacity: 1;
      transform: scale(1);
    }

    50% {
      opacity: 0.5;
      transform: scale(0.75);
    }
  }

  /* Extra bottom padding when sticky bar is present */
  .pb-queue {
    padding-bottom: 90px !important;
  }

  /* ── Sticky Queue Navigation Bar ── */
  .queue-nav-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1050;
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.18);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 32px;
    gap: 16px;
  }

  .btn-queue-nav {
    background: rgba(255, 255, 255, 0.15);
    border: 1.5px solid rgba(255, 255, 255, 0.35);
    color: #fff;
    border-radius: 10px;
    padding: 10px 28px;
    font-weight: 700;
    font-size: .9rem;
    letter-spacing: .02em;
    transition: background .15s;
    min-width: 110px;
  }

  .btn-queue-nav:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.28);
    color: #fff;
  }

  .btn-queue-nav:disabled {
    opacity: .3;
    cursor: not-allowed;
    color: #fff;
  }

  .queue-nav-center {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  .queue-nav-count {
    font-size: 1.15rem;
    font-weight: 800;
    color: #fff;
    line-height: 1;
  }

  .queue-nav-count .text-muted {
    color: rgba(255, 255, 255, .55) !important;
    font-weight: 400;
  }

  .queue-nav-label {
    font-size: .72rem;
    color: rgba(255, 255, 255, .65);
    letter-spacing: .06em;
    text-transform: uppercase;
  }

  /* ── Rejection Reasons Modal ── */
  .rejection-reasons-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .rejection-reason-btn {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    text-align: left;
    padding: 10px 14px;
    border: 1.5px solid #e9ecef;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    font-size: .88rem;
    color: #374151;
    transition: all .15s;
  }

  .rejection-reason-btn:hover {
    border-color: #fca5a5;
    background: #fff5f5;
    color: #b91c1c;
  }

  .rejection-reason-btn.selected {
    border-color: #ef4444;
    background: #fef2f2;
    color: #b91c1c;
    font-weight: 600;
  }

  .reason-num {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #f3f4f6;
    font-size: .72rem;
    font-weight: 700;
    color: #6b7280;
    flex-shrink: 0;
  }

  .rejection-reason-btn.selected .reason-num {
    background: #ef4444;
    color: #fff;
  }

  .reason-text {
    flex: 1;
  }

  .reason-check {
    color: #ef4444;
    font-size: .9rem;
    flex-shrink: 0;
  }

  /* ── Horizontal Request Timeline ── */
  .h-timeline {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    row-gap: 16px;
  }

  .h-timeline-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    min-width: 130px;
    flex: 0 0 auto;
  }

  .h-timeline-step--muted {
    opacity: .55;
  }

  .h-timeline-icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 16px;
    flex-shrink: 0;
    margin-bottom: 8px;
  }

  .h-timeline-icon--muted {
    background: #e2e8f0;
    color: #94a3b8;
  }

  .h-timeline-icon--indigo {
    background: #6366f1;
  }

  .h-timeline-connector {
    flex: 1 1 auto;
    height: 2px;
    min-width: 24px;
    background: #e2e8f0;
    margin-top: 18px;
  }

  .h-timeline-connector--done {
    background: #10b981;
  }

  .h-timeline-label strong {
    display: block;
    font-size: .85rem;
    color: #374151;
    margin-bottom: 2px;
  }

  .h-timeline-label span,
  .h-timeline-label a {
    display: block;
    font-size: .75rem;
    color: #64748b;
    line-height: 1.4;
  }
</style>